import { CAD_ITEM_COLLECTIONS } from "dot/Components/Content/configurationArea/Review/ManageCadDiscounts/cadPricing/cadItemCollections";
import {
  BoqLineItem,
  CadCategoryDefinition,
  CadDiscountCategoryGroup,
  GlobalCategoryDiscount,
} from "./types";

const extractCadCategoryResult = (
  payload: unknown
): Record<string, unknown> | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const root = payload as Record<string, unknown>;
  const nestedData =
    root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : undefined;
  const result = nestedData?.result ?? root.result;

  if (!result || typeof result !== "object" || Array.isArray(result)) {
    return null;
  }

  return result as Record<string, unknown>;
};

const humanizeCategoryKey = (key: string): string =>
  key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const categoryLabelFromApi = (id: string, value: unknown): string => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const label = (value as Record<string, unknown>).label;
    if (typeof label === "string" && label.trim()) {
      return label.trim();
    }
  }
  return humanizeCategoryKey(id);
};

/** Maps getCadCategories `data.result` into unique groups by `label`. */
export const mapCadCategoriesFromApi = (
  payload: unknown
): CadCategoryDefinition[] => {
  const result = extractCadCategoryResult(payload);
  if (!result) {
    return [];
  }

  const grouped: CadCategoryDefinition[] = [];
  const indexByLabel = new Map<string, number>();

  Object.entries(result).forEach(([id, value]) => {
    const label = categoryLabelFromApi(id, value);
    const existingIndex = indexByLabel.get(label);
    if (existingIndex === undefined) {
      indexByLabel.set(label, grouped.length);
      grouped.push({ id: label, label, sourceIds: [id] });
      return;
    }
    grouped[existingIndex].sourceIds = [
      ...(grouped[existingIndex].sourceIds ?? []),
      id,
    ];
  });

  return grouped;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const getCustomCategory = (row: Record<string, unknown>) =>
  row.custom_category ?? row.customCategory;

const toSnakeCase = (value: string): string =>
  value.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);

const toCamelCase = (value: string): string =>
  value.replace(/_([a-z])/g, (_, char: string) => char.toUpperCase());

const clampResponsePercent = (value: number): number =>
  Number.isNaN(value) ? 0 : Math.min(100, value);

const parseResponsePercent = (value: unknown): number => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? (() => {
            const trimmed = value.trim().replace(",", ".");
            const isNegative = trimmed.startsWith("-");
            const digits = trimmed.replace(/[^\d.]/g, "");
            return Number(`${isNegative ? "-" : ""}${digits || "0"}`);
          })()
        : NaN;
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return clampResponsePercent(parsed);
};

const percentFromKey = (
  key: string,
  percents: Record<string, number>
): number | undefined => {
  if (key in percents) {
    return percents[key];
  }
  const snake = toSnakeCase(key);
  if (snake in percents) {
    return percents[snake];
  }
  const camel = toCamelCase(key);
  if (camel in percents) {
    return percents[camel];
  }
  return undefined;
};

const categorySourceIds = (category: CadCategoryDefinition): string[] =>
  category.sourceIds?.length ? category.sourceIds : [category.id];

/** Resolves a waterfall `customCategory` key to the grouped category id (API label). */
export const resolveCategoryGroupId = (
  customCategory: string,
  categories: CadCategoryDefinition[] = []
): string => {
  const match = categories.find(
    (category) =>
      category.id === customCategory ||
      category.label === customCategory ||
      categorySourceIds(category).includes(customCategory)
  );
  return match?.id ?? customCategory;
};

/** Category CAD % from waterfall summary, matching the group label or source keys. */
export const cadPercentForCategory = (
  categoryId: string,
  percents: Record<string, number> = {},
  sourceIds: string[] = []
): number => {
  const keys = [categoryId, ...sourceIds];
  let fallback = 0;
  let matched = false;
  for (const key of keys) {
    const value = percentFromKey(key, percents);
    if (value === undefined) {
      continue;
    }
    matched = true;
    if (value !== 0) {
      return value;
    }
    fallback = value;
  }
  return matched ? fallback : 0;
};

const extractWaterfallSummary = (
  payload: unknown
): Record<string, unknown> | null => {
  const root = asRecord(payload);
  if (!root) {
    return null;
  }
  const nested = asRecord(root.data);
  const result = asRecord(nested?.result ?? root.result);
  return (
    asRecord(root.summary) ??
    asRecord(nested?.summary) ??
    asRecord(result?.summary) ??
    null
  );
};

/** Maps waterfall summary CAD percents by category key. Missing or invalid values are 0. */
export const mapGlobalCadPercentsFromWaterfall = (
  payload: unknown
): Record<string, number> => {
  const summary = extractWaterfallSummary(payload);
  const counts = asRecord(summary?.disPercentCounts);
  const cad = asRecord(counts?.competitive_additional_discount);
  if (!cad) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(cad).map(([key, value]) => [key, parseResponsePercent(value)])
  );
};

export const createGlobalDiscountsFromCategories = (
  categories: CadCategoryDefinition[],
  cadPercentByCategory: Record<string, number> = {}
): GlobalCategoryDiscount[] =>
  categories.map((category) => ({
    categoryId: category.id,
    selected: false,
    cadPercent: cadPercentForCategory(
      category.id,
      cadPercentByCategory,
      categorySourceIds(category)
    ),
    disabled: false,
  }));

const normalizedCadPercent = (value: unknown): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return clampResponsePercent(parsed);
};

/**
 * Sets each global CAD box from its line items.
 * Same percent on every item → that percent (enabled). Mixed percents → 0 and disabled.
 */
export const deriveGlobalCadFromLineItems = (
  globalDiscounts: GlobalCategoryDiscount[],
  categoryGroups: CadDiscountCategoryGroup[]
): GlobalCategoryDiscount[] => {
  const itemsByCategory = new Map(
    categoryGroups.map((group) => [group.categoryId, group.items])
  );

  return globalDiscounts.map((row) => {
    const items = itemsByCategory.get(row.categoryId) ?? [];
    if (items.length === 0) {
      return { ...row, cadPercent: 0, disabled: false };
    }
    const first = normalizedCadPercent(items[0].cadPercent);
    const allSame = items.every(
      (item) => normalizedCadPercent(item.cadPercent) === first
    );
    if (allSame) {
      return { ...row, cadPercent: first, disabled: false };
    }
    return { ...row, cadPercent: 0, selected: false, disabled: true };
  });
};

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toText = (value: unknown, fallback = ""): string => {
  if (value === undefined || value === null) {
    return fallback;
  }
  const text = String(value).trim();
  return text || fallback;
};

/**
 * Live BoQ rows from Redux. Canonical path is cartBoq.data.cart.items
 * (the Review cart / BoQ). Falls back to dynamic.boq if cart is empty.
 */
export const getCartBoqItems = (state: {
  cartBoq?: { data?: { cart?: { items?: unknown[] } } };
  dynamic?: { data?: { boq?: unknown } };
}): unknown[] => {
  const cartItems = state?.cartBoq?.data?.cart?.items;
  if (Array.isArray(cartItems) && cartItems.length > 0) {
    return cartItems.filter((item) => {
      const row = asRecord(item);
      return Boolean(row) && row?.type !== "";
    });
  }
  const boq = state?.dynamic?.data?.boq;
  return Array.isArray(boq) ? boq : [];
};

/** Same basis as the Review BoQ Total Price chip (`sum(cost)`). */
export const sumCartBoqCost = (cartItems: unknown[] = []): number => {
  const total = cartItems.reduce<number>((sum, item) => {
    const row = asRecord(item);
    const cost = Number(row?.cost);
    return sum + (Number.isFinite(cost) ? cost : 0);
  }, 0);
  return toNumber(total);
};

const cartItemMatchKeys = (row: Record<string, unknown>): string[] =>
  [
    toText(row.product_id),
    toText(row.productCode),
    toText(row.product_code),
    toText(row.si_code),
    toText(row.ootId),
  ].filter(Boolean);

/**
 * Writes CAD pricing onto matching BoQ cart rows.
 * Main BoQ List Price / Total Price read `cost`; NDAC also stores line total on `list_price`.
 */
export const applyCadPricingToCartItems = (
  cartItems: unknown[] = [],
  cadItems: Array<
    Pick<BoqLineItem, "id" | "sku" | "cadPercent" | "cadAmount" | "lineTotal">
  > = []
): unknown[] => {
  const remaining = [...cadItems];
  const takeCadItem = (keys: string[]) => {
    const index = remaining.findIndex(
      (cad) => keys.includes(cad.sku) || keys.includes(cad.id)
    );
    if (index < 0) {
      return undefined;
    }
    const [matched] = remaining.splice(index, 1);
    return matched;
  };

  return cartItems.map((item) => {
    const row = asRecord(item);
    if (!row) {
      return item;
    }
    const cad = takeCadItem(cartItemMatchKeys(row));
    if (!cad) {
      return item;
    }
    const itemTotal = toNumber(cad.lineTotal);
    return {
      ...row,
      cadPercent: cad.cadPercent,
      cadVal: toNumber(cad.cadAmount),
      itemTotal,
      cost: itemTotal,
      list_price: itemTotal,
    };
  });
};

const flattenPricingCollections = (
  source: Record<string, unknown> | null
): unknown[] => {
  if (!source) {
    return [];
  }

  return CAD_ITEM_COLLECTIONS.flatMap((key) => {
    const value = source[key];
    if (!Array.isArray(value)) {
      return [];
    }
    return value.map((item) => {
      const row = asRecord(item) ?? {};
      return {
        ...row,
        custom_category: getCustomCategory(row) ?? key,
      };
    });
  });
};

const extractWaterfallItemList = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  const root = asRecord(payload);
  if (!root) {
    return [];
  }

  if (Array.isArray(root.data)) {
    return root.data;
  }

  const nested = asRecord(root.data);
  const result = nested?.result ?? root.result;

  // Prefer the innermost grouped collections (result → data → root).
  const nestedCollections = flattenPricingCollections(asRecord(result));
  if (nestedCollections.length > 0) {
    return nestedCollections;
  }
  const dataCollections = flattenPricingCollections(nested);
  if (dataCollections.length > 0) {
    return dataCollections;
  }
  const rootCollections = flattenPricingCollections(root);
  if (rootCollections.length > 0) {
    return rootCollections;
  }

  const items = nested?.items ?? root.items;

  if (Array.isArray(items)) {
    return items;
  }
  if (Array.isArray(result)) {
    return result;
  }

  const resultObject = asRecord(result);
  if (resultObject) {
    const nestedItems = resultObject.items;
    if (Array.isArray(nestedItems)) {
      return nestedItems;
    }
    return Object.entries(resultObject).flatMap(([key, value]) => {
      if (!Array.isArray(value)) {
        return [];
      }
      return value.map((item) => {
        const row = asRecord(item) ?? {};
        return {
          ...row,
          custom_category: getCustomCategory(row) ?? key,
        };
      });
    });
  }

  return [];
};

const cartProductNameByCode = (cartItems: unknown[] = []) => {
  const names = new Map<string, string>();
  cartItems.forEach((item) => {
    const row = asRecord(item);
    if (!row) {
      return;
    }
    const code = toText(row.product_id);
    const name = toText(row.product_name);
    if (code && name) {
      names.set(code, name);
    }
  });
  return names;
};

type CartLineSnapshot = {
  quantity?: number;
};

const hasNumericValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== "";

/** Quantity keyed by product id, in cart order, so each BoQ row is used once. */
const cartLinesByCode = (cartItems: unknown[] = []) => {
  const lines = new Map<string, CartLineSnapshot[]>();
  cartItems.forEach((item) => {
    const row = asRecord(item);
    if (!row) {
      return;
    }
    const code = toText(row.product_id);
    if (!code || !hasNumericValue(row.quantity)) {
      return;
    }
    const list = lines.get(code) ?? [];
    list.push({
      quantity: Math.max(0, Math.floor(toNumber(row.quantity))),
    });
    lines.set(code, list);
  });
  return lines;
};

const takeCartLine = (
  lines: Map<string, CartLineSnapshot[]>,
  keys: string[]
): CartLineSnapshot | undefined => {
  for (const key of keys) {
    if (!key) {
      continue;
    }
    const list = lines.get(key);
    if (list && list.length > 0) {
      return list.shift();
    }
  }
  return undefined;
};

const mapWaterfallFromRow = (
  row: Record<string, unknown>
): BoqLineItem["waterfall"] => ({
  lpWithCad: toNumber(row.almp),
  plpAfterPtd: toNumber(row.pListPrice),
  plpAfterVd: toNumber(row.plpVd),
  plpAfterTbd: toNumber(row.plpTbd),
  pnpAfterSbd: toNumber(row.pNetPrice),
  pnpAfterDod: toNumber(row.pnpDod),
  pnpAfterDdp: toNumber(row.pnpIncoterms),
});

const mapWaterfallRowToLineItem = (
  item: unknown,
  index: number,
  productNames: Map<string, string> = new Map(),
  cartLines: Map<string, CartLineSnapshot[]> = new Map(),
  categories: CadCategoryDefinition[] = []
): BoqLineItem => {
  const row = asRecord(item) ?? {};
  const productCode = toText(row.productCode);
  const itemId = toText(row.itemId);
  const categoryId = resolveCategoryGroupId(
    toText(getCustomCategory(row), "uncategorized"),
    categories
  );
  const cadPercent = hasNumericValue(row.cadPercent)
    ? toNumber(row.cadPercent)
    : toNumber(row.cad_percent);
  const cadAmount = toNumber(row.cadVal);
  const cartLine = takeCartLine(cartLines, [productCode, itemId]);
  const quantity = Math.max(
    0,
    Math.floor(cartLine?.quantity ?? toNumber(row.quantity))
  );
  const nlp = toNumber(row.pnpIncoterms);
  const dealPrice = nlp;
  const lineTotal = toNumber(row.itemTotal);
  const idBase = itemId || productCode || "item";
  const mlp = toNumber(row.mlp);
  return {
    id: `${idBase}-${index}`,
    categoryId,
    productName: toText(
      row.product_name ?? productNames.get(productCode) ?? productCode,
      "Untitled item"
    ),
    sku: productCode,
    glp: toNumber(row.glp),
    nlp,
    cadPercent,
    cadAmount,
    dealPrice,
    lineTotal,
    quantity,
    selected: false,
    mlp,
    expanded: false,
    waterfall: mapWaterfallFromRow(row),
  };
};

/**
 * Groups submitWaterfall rows by the getCadCategories label (shared keys merge).
 */
export const mapWaterfallItemsToCategoryGroups = (
  payload: unknown,
  categories: CadCategoryDefinition[] = [],
  cartItems: unknown[] = []
): CadDiscountCategoryGroup[] => {
  const productNames = cartProductNameByCode(cartItems);
  const cartLines = cartLinesByCode(cartItems);
  const items = extractWaterfallItemList(payload).map((item, index) =>
    mapWaterfallRowToLineItem(item, index, productNames, cartLines, categories)
  );
  const itemsByCategory = items.reduce<Record<string, BoqLineItem[]>>(
    (acc, item) => {
      if (!acc[item.categoryId]) {
        acc[item.categoryId] = [];
      }
      acc[item.categoryId].push(item);
      return acc;
    },
    {}
  );

  const orderedIds = [
    ...categories.map((category) => category.id),
    ...Object.keys(itemsByCategory).filter(
      (id) => !categories.some((category) => category.id === id)
    ),
  ];

  const labelById = categories.reduce<Record<string, string>>((acc, category) => {
    acc[category.id] = category.label;
    return acc;
  }, {});

  return orderedIds
    .filter((id) => (itemsByCategory[id] || []).length > 0)
    .map((id) => ({
      categoryId: id,
      label: labelById[id] ?? humanizeCategoryKey(id),
      items: itemsByCategory[id],
    }));
};
